import { useEffect, useMemo, useState } from "react";
import { MessageCircle, X } from "lucide-react";
import { useLocation } from "react-router-dom";
import { api, errMsg } from "../lib/api";
import { toast } from "sonner";
import { useAuth } from "../context/AuthContext";
import { ChatPanel } from "./ChatPanel";
import { Button } from "./ui/button";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";

const STATUS_LABEL = { baru: "Baru", diproses: "Diproses", selesai: "Selesai" };

export function FloatingChat() {
  const { user: me } = useAuth();
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [channel, setChannel] = useState("");
  const [tickets, setTickets] = useState([]);
  const [threads, setThreads] = useState([]);
  const [unread, setUnread] = useState(0);
  const [loaded, setLoaded] = useState(false);
  const isAdmin = ["admin", "admin_fasnet"].includes(me?.role);
  const hidden = location.pathname === "/komplain-wifi";

  useEffect(() => {
    const onOpen = (e) => {
      if (e.detail) { setChannel(e.detail); setOpen(true); }
    };
    window.addEventListener("fasnet:open-chat", onOpen);
    return () => window.removeEventListener("fasnet:open-chat", onOpen);
  }, []);

  useEffect(() => {
    if (!me?.id) return undefined;
    let alive = true;
    const load = () => api.get("/chat/unread")
      .then((r) => { if (alive) setUnread(Object.values(r.data || {}).reduce((a, b) => a + b, 0)); })
      .catch(() => {});
    load();
    const t = setInterval(load, 15000);
    return () => { alive = false; clearInterval(t); };
  }, [me?.id, open]);

  useEffect(() => {
    if (!open || !me?.id) return undefined;
    let alive = true;
    const load = async () => {
      try {
        const reqs = [api.get("/wifi-complaints")];
        if (isAdmin) reqs.push(api.get("/chat/agent-threads"));
        const [rc, rt] = await Promise.all(reqs);
        if (!alive) return;
        setTickets(rc.data);
        if (rt) setThreads(rt.data);
        setLoaded(true);
      } catch { /* retry di interval berikutnya */ }
    };
    load();
    const t = setInterval(load, 20000);
    return () => { alive = false; clearInterval(t); };
  }, [open, me?.id, isAdmin]);

  const options = useMemo(() => {
    const opts = [];
    tickets.filter((c) => c.status !== "selesai" || `complaint:${c.id}` === channel).forEach((c) => {
      opts.push({ value: `complaint:${c.id}`, label: `Komplain ${c.ticket_no || ""} — ${c.name} (${STATUS_LABEL[c.status] || c.status})` });
    });
    if (isAdmin) {
      threads.forEach((t) => {
        if (t.channel.startsWith("complaint:")) return;
        const kind = t.channel.split(":")[0];
        const label = kind === "system" ? `Sistem — ${t.channel.split(":")[1]}` : t.pending ? `Aktivasi — ${t.agent_name}` : t.agent_name;
        opts.push({ value: t.channel, label: t.unread ? `${label} (${t.unread} baru)` : label });
      });
    } else {
      opts.push({ value: `agent:${me?.id}`, label: "Chat Admin" });
    }
    return opts;
  }, [tickets, threads, isAdmin, me?.id, channel]);

  const currentTicket = useMemo(() => tickets.find((c) => `complaint:${c.id}` === channel), [tickets, channel]);

  const setTicketStatus = async (status) => {
    if (!currentTicket) return;
    try {
      await api.put(`/wifi-complaints/${currentTicket.id}`, {
        status,
        handled_by: currentTicket.handled_by || "",
        estimation: currentTicket.estimation || "",
        tindakan: currentTicket.tindakan || "",
      });
      setTickets((ts) => ts.map((t) => (t.id === currentTicket.id ? { ...t, status } : t)));
      toast.success(`Tiket ${currentTicket.ticket_no || ""} ditandai ${STATUS_LABEL[status]}`);
    } catch (e) { toast.error(errMsg(e)); }
  };

  useEffect(() => {
    if (open && loaded && options.length && (!channel || !options.some((o) => o.value === channel))) setChannel(options[0].value);
  }, [open, loaded, options, channel]);

  if (!me?.id || hidden) return null;

  return (
    <>
      {open && (
        <div className="fixed z-40 bottom-[5.5rem] right-4 lg:bottom-20 lg:right-6 w-[calc(100vw-2rem)] max-w-sm h-[65vh] max-h-[540px] bg-card border border-border rounded-2xl shadow-2xl flex flex-col overflow-hidden" data-testid="floating-chat-panel">
          <div className="flex items-center gap-2 px-3 py-2 border-b border-border bg-accent/40">
            <Select value={channel} onValueChange={setChannel}>
              <SelectTrigger className="h-8 text-xs flex-1" data-testid="floating-chat-select"><SelectValue placeholder="Pilih percakapan..." /></SelectTrigger>
              <SelectContent>
                {options.map((o) => <SelectItem key={o.value} value={o.value} data-testid={`floating-chat-opt-${o.value.replace(/[^a-zA-Z0-9]/g, "-")}`}>{o.label}</SelectItem>)}
              </SelectContent>
            </Select>
            <Button size="icon" variant="ghost" className="h-8 w-8" onClick={() => setOpen(false)} data-testid="floating-chat-close"><X className="w-4 h-4" /></Button>
          </div>
          {isAdmin && currentTicket && (
            <div className="flex items-center gap-2 px-3 py-1.5 border-b border-border" data-testid="floating-chat-status-bar">
              <span className="text-[10px] font-mono text-muted-foreground">Status: <span className="font-bold text-foreground">{STATUS_LABEL[currentTicket.status] || currentTicket.status}</span></span>
              <div className="flex-1" />
              {currentTicket.status === "baru" && (
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] text-sky-400 border-sky-500/30 hover:bg-sky-500/10" onClick={() => setTicketStatus("diproses")} data-testid="floating-chat-btn-diproses">Diproses</Button>
              )}
              {currentTicket.status !== "selesai" && (
                <Button size="sm" variant="outline" className="h-6 px-2 text-[10px] text-emerald-400 border-emerald-500/30 hover:bg-emerald-500/10" onClick={() => setTicketStatus("selesai")} data-testid="floating-chat-btn-selesai">Selesai</Button>
              )}
            </div>
          )}
          <div className="flex-1 min-h-0 p-3">
            {channel ? <ChatPanel channel={channel} testid="floating-chat" compact /> : <div className="text-center text-xs text-muted-foreground py-10">Memuat percakapan...</div>}
          </div>
        </div>
      )}
      <button type="button" onClick={() => setOpen(!open)}
        className="fixed z-40 bottom-24 right-4 lg:bottom-6 lg:right-6 h-12 w-12 rounded-full bg-[#F04E23] hover:bg-[#d94418] text-white shadow-lg shadow-[#F04E23]/30 flex items-center justify-center transition-transform hover:scale-105 active:scale-95"
        data-testid="floating-chat-button" title="Chat cepat — tetap terbuka saat berpindah menu">
        {open ? <X className="w-5 h-5" /> : <MessageCircle className="w-5 h-5" />}
        {!open && unread > 0 && (
          <span className="absolute -top-1 -right-1 min-w-5 h-5 px-1 rounded-full bg-rose-500 text-white text-[10px] font-bold flex items-center justify-center border-2 border-background" data-testid="floating-chat-badge">{unread > 99 ? "99+" : unread}</span>
        )}
      </button>
    </>
  );
}
